import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar, Toolbar, Box, Container, Typography, Button, IconButton, Drawer,
  List, ListItem, ListItemButton, ListItemText, Stack, Divider, Avatar, Menu,
  MenuItem, ListItemIcon, Tooltip, Chip,
} from '@mui/material';
import {
  Menu as MenuIcon, Close as CloseIcon, Cloud, PersonOutlineOutlined,
  AccountCircleOutlined, LockResetOutlined, ShoppingCartOutlined,
  PaymentOutlined, LogoutOutlined, KeyboardArrowDown, Lock,
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { C } from './theme';

const VpsNavbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [accountMenuAnchor, setAccountMenuAnchor] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

  const navItems = [
    { label: 'Home', target: 'hero' },
    { label: 'Pricing', target: 'pricing' },
    // { label: 'Comparison', target: 'comparison' },
    { label: 'Features', target: 'features' },
    { label: 'One-Click Deploy', target: 'deploy' },
    { label: 'Reviews', target: 'reviews' },
    { label: 'FAQ', target: 'faq' },
    { label: 'Migration', target: 'migration' },
  ];

  const accountMenuItems = [
    {
      label: 'Profile',
      icon: <AccountCircleOutlined fontSize="small" />,
      path: '/account/profile',
    },
    {
      label: 'Change Password',
      icon: <LockResetOutlined fontSize="small" />,
      path: '/account/change-password',
    },
    {
      label: 'Orders',
      icon: <ShoppingCartOutlined fontSize="small" />,
      path: '/account/orders',
    },
    {
      label: 'Payments',
      icon: <PaymentOutlined fontSize="small" />,
      path: '/account/payments',
    },
  ];

  const handleItemClick = (target) => {
    setMobileOpen(false);
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.getElementById(target);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.getElementById(target);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      } else {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  };

  const handleLogoClick = () => {
    if (location.pathname !== '/') {
      navigate('/');
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleAccountMenuOpen = (e) => {
    setAccountMenuAnchor(e.currentTarget);
  };

  const handleAccountMenuClose = () => {
    setAccountMenuAnchor(null);
  };

  const handleAccountNavigation = (path) => {
    handleAccountMenuClose();
    navigate(path);
  };

  const handleLogout = () => {
    handleAccountMenuClose();
    logout();
    navigate('/');
  };

  return (
    <>
      {/* ── Top Announcement Bar ── */}
      <Box sx={{ background: '#1E293B', py: 0.9, color: '#FFFFFF' }}>
        <Container maxWidth="xl">
          <Typography align="center" sx={{ fontSize: '0.82rem', fontWeight: 500 }}>
            🤖 ChatGPT, Claude, Gemini, Grok — all four for <strong>₹1,894/mo</strong>
            <Box component="span"
              onClick={() => handleItemClick('pricing')}
              sx={{ textDecoration: 'underline', cursor: 'pointer', ml: 1.5, opacity: 0.9, fontWeight: 600 }}>
              Get All Access →
            </Box>
          </Typography>
        </Container>
      </Box>

      {/* ── Main Header / Navbar ── */}
      <AppBar position="sticky" elevation={0} sx={{
        background: '#FFFFFF',
        borderBottom: `1px solid ${C.border}`,
        color: C.textPrimary,
        zIndex: 1100,
        height: { xs: 64, md: 70 },
        overflow: 'hidden',
      }}>
        <Container maxWidth="xl" sx={{ height: '100%' }}>
          <Toolbar disableGutters sx={{ height: '100%', minHeight: 'unset', justifyContent: 'space-between', gap: 2 }}>

            {/* Brand Logo — design logo to: 200×44px (desktop) / 160×40px (mobile) */}
            <Box
              component="img"
              src="/assets/vps-logo/logo-without-bg.png"
              alt="Hosting Services"
              onClick={handleLogoClick}
              sx={{
                width: { xs: 160, md: 180 },
                height: { xs: 100, md: 124 },
                objectFit: 'contain',
                objectPosition: 'left center',
                cursor: 'pointer',
                display: 'block',
                flexShrink: 0,
              }}
            />

            {/* Desktop Navigation Links with Smooth Scrolling */}
            <Stack direction="row" spacing={0.5} sx={{ display: { xs: 'none', lg: 'flex' } }}>
              {navItems.map((item) => (
                <Button key={item.label}
                  onClick={() => handleItemClick(item.target)}
                  sx={{
                    color: C.textPrimary, fontSize: '0.88rem', fontWeight: 600,
                    textTransform: 'none', px: 1.5, py: 0.8, borderRadius: '8px',
                    '&:hover': { background: C.bgSection, color: C.primary },
                  }}>
                  {item.label}
                </Button>
              ))}
            </Stack>

            {/* Right Account & Action Buttons */}
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ display: { xs: 'none', md: 'flex' } }}>
              {user ? (
                <Tooltip title="My Account" arrow>
                  <Button
                    id="account-menu-btn"
                    onClick={handleAccountMenuOpen}
                    endIcon={<KeyboardArrowDown sx={{ fontSize: '18px !important' }} />}
                    sx={{
                      gap: 1,
                      borderRadius: '8px',
                      px: 1.5, py: 0.75,
                      color: C.textPrimary,
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: '0.88rem',
                      '&:hover': { background: C.bgSection },
                    }}
                  >
                    <Avatar sx={{ width: 30, height: 30, background: C.primary, fontSize: '0.82rem', fontWeight: 800 }}>
                      {user.avatar || user.name[0]}
                    </Avatar>
                    <Box sx={{ textAlign: 'left' }}>
                      <Typography variant="caption" sx={{ fontWeight: 700, color: C.textPrimary, display: 'block', lineHeight: 1.2 }}>
                        {user.name.length > 12 ? user.name.slice(0, 12) + '…' : user.name}
                      </Typography>
                      <Typography variant="caption" sx={{ color: C.textMuted, fontSize: '0.68rem', lineHeight: 1 }}>
                        My Account
                      </Typography>
                    </Box>
                  </Button>
                </Tooltip>
              ) : (
                <Button
                  startIcon={<PersonOutlineOutlined />}
                  variant="outlined"
                  size="small"
                  onClick={() => navigate('/login')}
                  sx={{
                    borderColor: C.border,
                    color: C.textPrimary,
                    borderRadius: '8px',
                    px: 1.8, py: 0.75,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '0.84rem',
                    '&:hover': { borderColor: C.primary, background: C.primaryLight, color: C.primary },
                  }}
                >
                  My Account
                </Button>
              )}

              <Button variant="contained" size="small"
                onClick={() => handleItemClick('pricing')}
                sx={{
                  background: C.primary, color: '#FFFFFF', textTransform: 'none',
                  borderRadius: '8px', fontSize: '0.84rem', fontWeight: 700, px: 2.2, py: 0.75,
                  boxShadow: 'none',
                  '&:hover': { background: C.primaryHover, boxShadow: 'none' },
                }}>
                View Pricing
              </Button>
            </Stack>

            {/* Mobile Hamburger */}
            <IconButton onClick={handleDrawerToggle} sx={{ display: { lg: 'none' }, color: C.textPrimary }}>
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </Container>
      </AppBar>

      {/* ── User Account Dropdown Menu ── */}
      <Menu
        anchorEl={accountMenuAnchor}
        open={Boolean(accountMenuAnchor)}
        onClose={handleAccountMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          elevation: 4,
          sx: {
            mt: 1, minWidth: 220, borderRadius: '12px',
            border: `1px solid ${C.border}`, overflow: 'visible',
            boxShadow: '0 12px 40px rgba(0,0,0,0.1)',
          },
        }}
      >
        {user && (
          <Box sx={{ px: 2.5, py: 1.5, borderBottom: `1px solid ${C.border}` }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ width: 36, height: 36, background: C.primary, fontWeight: 800, fontSize: '0.9rem' }}>
                {user.avatar || user.name[0]}
              </Avatar>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: C.textPrimary, lineHeight: 1.2 }}>
                  {user.name}
                </Typography>
                <Typography variant="caption" sx={{ color: C.textMuted, fontSize: '0.74rem' }}>
                  {user.email}
                </Typography>
              </Box>
            </Stack>
          </Box>
        )}

        <Box sx={{ py: 1 }}>
          {accountMenuItems.map((item) => (
            <MenuItem
              key={item.label}
              onClick={() => handleAccountNavigation(item.path)}
              sx={{
                mx: 1, borderRadius: '8px', py: 1, px: 1.5,
                '&:hover': { background: C.bgSection, color: C.primary },
              }}
            >
              <ListItemIcon sx={{ minWidth: 32, color: C.textMuted }}>
                {item.icon}
              </ListItemIcon>
              <Typography variant="body2" sx={{ fontWeight: 600, color: 'inherit' }}>
                {item.label}
              </Typography>
            </MenuItem>
          ))}
        </Box>

        <Divider sx={{ borderColor: C.border, mx: 1.5 }} />

        <Box sx={{ py: 1 }}>
          <MenuItem
            onClick={handleLogout}
            sx={{
              mx: 1, borderRadius: '8px', py: 1, px: 1.5,
              '&:hover': { background: '#FEF2F2', color: '#DC2626' },
            }}
          >
            <ListItemIcon sx={{ minWidth: 32, color: '#F87171' }}>
              <LogoutOutlined fontSize="small" />
            </ListItemIcon>
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'inherit' }}>
              Sign Out
            </Typography>
          </MenuItem>
        </Box>
      </Menu>

      {/* ── Mobile Drawer ── */}
      <Drawer
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        slotProps={{
          paper: {
            sx: {
              width: 300,
              backgroundColor: "#fff",
              p: 1,
            },
          },
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box
            component="img"
            src="/assets/vps-logo/logo-without-bg.png"
            alt="Hosting Services"
            sx={{ height: 86, width: 'auto', objectFit: 'contain' }}
          />
          <IconButton onClick={handleDrawerToggle} sx={{ color: C.textPrimary }}><CloseIcon /></IconButton>
        </Box>
        <Divider sx={{ borderColor: C.border, mb: 2 }} />

        <List disablePadding>
          {navItems.map((item) => (
            <ListItem key={item.label} disablePadding>
              <ListItemButton onClick={() => handleItemClick(item.target)} sx={{ borderRadius: '8px', py: 1, '&:hover': { background: C.bgSection } }}>
                <ListItemText primary={item.label} primaryTypographyProps={{ color: C.textPrimary, fontWeight: 600, fontSize: '0.95rem' }} />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        <Divider sx={{ borderColor: C.border, my: 2 }} />

        {user ? (
          <Stack spacing={1}>
            {accountMenuItems.map((item) => (
              <ListItemButton key={item.label} onClick={() => { setMobileOpen(false); navigate(item.path); }} sx={{ borderRadius: '8px' }}>
                <ListItemIcon sx={{ minWidth: 32 }}>{item.icon}</ListItemIcon>
                <ListItemText primary={item.label} primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: 600 }} />
              </ListItemButton>
            ))}
            <Button variant="outlined" color="error" fullWidth onClick={() => { setMobileOpen(false); handleLogout(); }} sx={{ mt: 1, textTransform: 'none', borderRadius: '8px' }}>
              Sign Out
            </Button>
          </Stack>
        ) : (
          <Stack spacing={1.5}>
            <Button variant="outlined" fullWidth onClick={() => { setMobileOpen(false); navigate('/login'); }} sx={{ borderColor: C.border, color: C.textPrimary, textTransform: 'none', borderRadius: '8px', fontWeight: 600 }}>
              My Account / Login
            </Button>
            <Button variant="contained" fullWidth onClick={() => handleItemClick('pricing')} sx={{ background: C.primary, color: '#FFFFFF', textTransform: 'none', borderRadius: '8px', fontWeight: 700 }}>
              View Pricing
            </Button>
          </Stack>
        )}
      </Drawer>
    </>
  );
};

export default VpsNavbar;
